// FILE: src/components/LearnerAuth.js
//
// Sign-in / sign-up modal for learners. Mounted as a portal so it overlays
// any view without disrupting the rest of App.js state.
//
// Tabs:
//   • Sign in  (default) — OAuth + email/password
//   • Sign up           — email/password + display name
//   • Reset             — password reset request
//
// Voice is calm-mentor per §6.5 of the design doc. No "you must" / no urgency.

import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './LearnerAuth.module.css';
import {
  signUpWithEmail,
  signInWithEmail,
  signInWithGoogle,
  signInWithMicrosoft,
  sendPasswordResetEmail,
  resendConfirmationEmail,
} from '../../services/learnerAuthService';
import { trackEvent } from '../../services/telemetryService';

const TAB = { SIGNIN: 'signin', SIGNUP: 'signup', RESET: 'reset' };

export default function LearnerAuth({ isOpen, initialTab = TAB.SIGNIN, onClose, onSuccess }) {
  const [tab, setTab]                 = useState(initialTab);
  const [email, setEmail]             = useState('');
  const [password, setPassword]       = useState('');
  const [confirmPwd, setConfirmPwd]   = useState('');
  const [displayName, setDisplayName] = useState('');
  const [busy, setBusy]               = useState(false);
  const [error, setError]             = useState(null);
  const [info, setInfo]               = useState(null); // success-side info ("Check your email")
  // Unconfirmed-email recovery (activation Phase 2): offer a one-click resend
  // when sign-in fails with "Email not confirmed". 'sent' disables the control
  // for the rest of the modal session (server rate limits the rest).
  const [confirmResend, setConfirmResend] = useState(null); // null | 'offer' | 'sending' | 'sent'
  const dialogRef                     = useRef(null);

  // Reset transient state when modal opens/closes or tab changes.
  useEffect(() => {
    if (!isOpen) return;
    setTab(initialTab);
    setError(null);
    setInfo(null);
  }, [isOpen, initialTab]);

  // Close on Escape.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  async function handleSignIn(e) {
    e.preventDefault();
    setBusy(true); setError(null); setInfo(null);
    const { error: err } = await signInWithEmail({ email, password });
    setBusy(false);
    if (err) {
      trackEvent('signin_failed', 'growth');
      setError(humanizeError(err, 'signin'));
      if ((err.message || '').toLowerCase().includes('email not confirmed')) {
        setConfirmResend((prev) => (prev === 'sent' ? 'sent' : 'offer'));
      }
      return;
    }
    setConfirmResend(null);
    onSuccess?.();
    onClose?.();
  }

  async function handleSignUp(e) {
    e.preventDefault();
    setBusy(true); setError(null); setInfo(null);

    if (password.length < 8) {
      setBusy(false);
      setError('Choose a password with at least 8 characters.');
      return;
    }
    if (password !== confirmPwd) {
      setBusy(false);
      setError('Passwords don’t match.');
      return;
    }

    trackEvent('register_started', 'growth', { method: 'email' });
    const { error: err } = await signUpWithEmail({ email, password, displayName });
    setBusy(false);
    if (err) {
      setError(humanizeError(err, 'signup'));
      return;
    }
    setInfo('Check your email — we sent a confirmation link. You can keep using the app while you wait.');
  }

  async function handleReset(e) {
    e.preventDefault();
    setBusy(true); setError(null); setInfo(null);
    const { error: err } = await sendPasswordResetEmail(email);
    setBusy(false);
    if (err) {
      setError(humanizeError(err, 'reset'));
      return;
    }
    setInfo('Check your email for a reset link.');
  }

  async function handleOAuth(which) {
    setBusy(true); setError(null); setInfo(null);

    // On the Sign Up tab the user is intentionally registering — emit
    // register_started for funnel parity with the email/password path.
    // On the Sign In tab we don't know if they're a new or returning user
    // until they're back, so we stay quiet here; register_completed still
    // fires on first profile creation in LearnerAuthContext.
    if (tab === TAB.SIGNUP) {
      trackEvent('register_started', 'growth', { method: which });
    }

    const call = which === 'google' ? signInWithGoogle : signInWithMicrosoft;
    const { error: err } = await call();
    setBusy(false);
    if (err) {
      setError(humanizeError(err, 'oauth'));
    }
    // Successful OAuth navigates away; nothing else to do here.
  }

  const showOAuth = tab !== TAB.RESET;
  const title =
    tab === TAB.SIGNUP ? 'Create your account' :
    tab === TAB.RESET  ? 'Reset your password' :
                         'Welcome back';

  return createPortal(
    <div
      className={styles.backdrop}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
      data-testid="learner-auth-backdrop"
    >
      <div
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="learner-auth-title"
        ref={dialogRef}
      >
        <button
          type="button"
          className={styles.closeBtn}
          aria-label="Close sign-in"
          onClick={() => onClose?.()}
          data-testid="learner-auth-close"
        >
          ×
        </button>

        <h2 id="learner-auth-title" className={styles.title}>{title}</h2>
        <p className={styles.subtitle}>
          {tab === TAB.SIGNUP
            ? 'Your sessions and journey will be saved to your account.'
            : tab === TAB.RESET
              ? 'Enter your email — we’ll send a reset link.'
              : 'Sign in to track your certification journey.'}
        </p>

        <p className={styles.subtitle} style={{ fontSize: '12px', marginTop: '-4px' }} data-testid="learner-auth-legal">
          By continuing, you agree to the{' '}
          <a href="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>{' '}
          and acknowledge the{' '}
          <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
        </p>

        {showOAuth && (
          <div className={styles.oauthGroup}>
            <button
              type="button"
              className={styles.oauthBtn}
              onClick={() => handleOAuth('google')}
              disabled={busy}
              data-testid="learner-auth-google"
            >
              Continue with Google
            </button>
            <button
              type="button"
              className={styles.oauthBtn}
              onClick={() => handleOAuth('microsoft')}
              disabled={busy}
              data-testid="learner-auth-microsoft"
            >
              Continue with Microsoft
            </button>
            <div className={styles.divider}><span>or</span></div>
          </div>
        )}

        {tab === TAB.SIGNIN && (
          <form className={styles.form} onSubmit={handleSignIn} data-testid="learner-auth-signin-form">
            <label className={styles.label}>
              Email
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                disabled={busy}
                data-testid="learner-auth-email"
              />
            </label>
            <label className={styles.label}>
              Password
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                disabled={busy}
                data-testid="learner-auth-password"
              />
            </label>
            <button
              type="submit"
              className={styles.primaryBtn}
              disabled={busy}
              data-testid="learner-auth-signin-submit"
            >
              {busy ? 'Signing in…' : 'Sign in'}
            </button>
            <div className={styles.linkRow}>
              <button
                type="button"
                className={styles.linkBtn}
                onClick={() => setTab(TAB.RESET)}
                disabled={busy}
              >
                Forgot password?
              </button>
              <button
                type="button"
                className={styles.linkBtn}
                onClick={() => setTab(TAB.SIGNUP)}
                disabled={busy}
                data-testid="learner-auth-switch-to-signup"
              >
                Create an account
              </button>
            </div>
          </form>
        )}

        {tab === TAB.SIGNUP && (
          <form className={styles.form} onSubmit={handleSignUp} data-testid="learner-auth-signup-form">
            <label className={styles.label}>
              Email
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                disabled={busy}
              />
            </label>
            <label className={styles.label}>
              Password
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="new-password"
                minLength={8}
                disabled={busy}
              />
            </label>
            <label className={styles.label}>
              Confirm password
              <input
                type="password"
                value={confirmPwd}
                onChange={(e) => setConfirmPwd(e.target.value)}
                required
                autoComplete="new-password"
                minLength={8}
                disabled={busy}
              />
            </label>
            <label className={styles.label}>
              Display name <span className={styles.optional}>(optional)</span>
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                disabled={busy}
              />
            </label>
            <button
              type="submit"
              className={styles.primaryBtn}
              disabled={busy}
              data-testid="learner-auth-signup-submit"
            >
              {busy ? 'Creating account…' : 'Create account'}
            </button>
            <div className={styles.linkRow}>
              <button
                type="button"
                className={styles.linkBtn}
                onClick={() => setTab(TAB.SIGNIN)}
                disabled={busy}
              >
                Already have an account? Sign in
              </button>
            </div>
          </form>
        )}

        {tab === TAB.RESET && (
          <form className={styles.form} onSubmit={handleReset} data-testid="learner-auth-reset-form">
            <label className={styles.label}>
              Email
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                disabled={busy}
              />
            </label>
            <button
              type="submit"
              className={styles.primaryBtn}
              disabled={busy}
            >
              {busy ? 'Sending…' : 'Send reset link'}
            </button>
            <div className={styles.linkRow}>
              <button
                type="button"
                className={styles.linkBtn}
                onClick={() => setTab(TAB.SIGNIN)}
                disabled={busy}
              >
                Back to sign in
              </button>
            </div>
          </form>
        )}

        {error && (
          <div className={styles.errorBox} role="alert" data-testid="learner-auth-error">
            {error}
          </div>
        )}
        {confirmResend && confirmResend !== 'sent' && (
          <button
            type="button"
            className={styles.linkBtn}
            data-testid="learner-auth-resend-confirmation"
            disabled={busy || confirmResend === 'sending' || !email}
            onClick={async () => {
              setConfirmResend('sending');
              trackEvent('confirmation_resend_requested', 'growth');
              const { error: err } = await resendConfirmationEmail(email);
              if (err) {
                setConfirmResend('offer');
                setError(humanizeError(err, 'signin'));
                return;
              }
              setConfirmResend('sent');
              setError(null);
              setInfo('Confirmation email sent — check your inbox (and spam). The link works for 1 hour.');
            }}
          >
            {confirmResend === 'sending' ? 'Sending…' : 'Resend the confirmation email'}
          </button>
        )}
        {info && (
          <div className={styles.infoBox} role="status" data-testid="learner-auth-info">
            {info}
          </div>
        )}

        <p className={styles.footnote}>
          You can keep using PM Pixel without an account — sign in only when you’re ready to save your progress.
        </p>
      </div>
    </div>,
    document.body,
  );
}

// Map common Supabase auth error shapes to coach-toned copy. Never blame.
function humanizeError(err, ctx) {
  const msg = (err?.message || '').toLowerCase();
  if (msg.includes('invalid login')) return 'That email and password didn’t match. Try again or use a reset link.';
  if (msg.includes('email not confirmed')) return 'Almost there — please confirm your email first. We sent a link when you signed up.';
  if (msg.includes('already registered') || msg.includes('user already'))
    return 'That email already has an account. Sign in instead, or reset the password.';
  if (msg.includes('rate limit')) return 'Too many tries in a short window. Try again in a minute.';
  if (msg.includes('popup')) return 'The sign-in popup was blocked. Allow popups for this site and try again.';
  if (msg.includes('network')) return 'Network hiccup — please try again.';
  if (msg) return err.message;
  return ctx === 'signup'
    ? 'Couldn’t create the account right now. Please try again.'
    : 'Couldn’t complete that just now. Please try again.';
}

LearnerAuth.TAB = TAB;
